// Drafting a TREC contract from a deal (ticket 206): the hub's "Draft from a deal" dialog
// and the contracts list on the deal page. Both call POST /re/contracts/auto-draft, which
// fills the parties, address, price, deposit, option and closing date from the saved deal.
// A draft is internal: nothing is sent to anyone.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileSignature, Loader2, Plus, ExternalLink, ChevronRight } from 'lucide-react';
import { api, openWithAuth } from '../api';
import { toast } from '../../hooks/use-toast';
import { errorMessage } from '../errors';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { dealLabel } from './format';

const DEFAULT_TEMPLATE = '20_17';

// Which signers the draft will have: the deal's buyer and seller emails decide it.
export function missingSignerHint(tx) {
  const missing = [];
  if (!tx?.buyer_email) missing.push('buyer');
  if (!tx?.seller_email) missing.push('seller');
  if (!missing.length) return '';
  return `No ${missing.join(' or ')} email on this deal, so the draft starts without ${missing.length > 1 ? 'those signers' : `the ${missing[0]} as a signer`}. Add the email on the deal page, or add signers on the contract.`;
}

async function draft(dealId, templateKey) {
  const { data } = await api.post('/re/contracts/auto-draft', { deal_id: dealId, template_key: templateKey });
  return data;
}

function TemplateSelect({ templates, value, onChange, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="mt-1" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>{templates.map((t) => <SelectItem key={t.template_key} value={t.template_key}>{t.title}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function useTemplates() {
  const [templates, setTemplates] = useState([]);
  useEffect(() => {
    api.get('/re/contract-templates').then((r) => setTemplates(Array.isArray(r.data) ? r.data : [])).catch(() => setTemplates([]));
  }, []);
  return templates;
}

export function AutoDraftModal({ templates, onClose, onDrafted }) {
  const [txs, setTxs] = useState(null);
  const [dealId, setDealId] = useState('');
  const [templateKey, setTemplateKey] = useState(DEFAULT_TEMPLATE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    api.get('/re/transactions').then((r) => setTxs(Array.isArray(r.data) ? r.data : [])).catch(() => setTxs([]));
  }, []);
  const tx = (txs || []).find((t) => t.transaction_id === dealId);
  const submit = async () => {
    if (saving) return;
    if (!dealId) { setError('Pick the deal to draft from.'); return; }
    setError(''); setSaving(true);
    try {
      onDrafted(await draft(dealId, templateKey));
    } catch (e) {
      setError(errorMessage(e));
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="auto-draft-modal">
        <div className="font-serif text-lg font-bold mb-1" style={{ color: '#070B5F' }}>Draft a contract from a deal</div>
        <p className="text-xs text-slate-500 mb-4">Fills the parties, address, price, earnest money, option and closing date from the deal. Nothing is sent to anyone.</p>
        <div className="space-y-3">
          <div>
            <div className="text-xs font-medium">Deal</div>
            <Select value={dealId} onValueChange={(v) => { setDealId(v); setError(''); }}>
              <SelectTrigger className="mt-1" data-testid="auto-draft-deal"><SelectValue placeholder={txs === null ? 'Loading deals…' : 'Pick a deal'} /></SelectTrigger>
              <SelectContent>{(txs || []).map((t) => <SelectItem key={t.transaction_id} value={t.transaction_id}>{dealLabel(t)}</SelectItem>)}</SelectContent>
            </Select>
            {tx && missingSignerHint(tx) && <div className="text-xs text-amber-700 mt-1" data-testid="auto-draft-hint">{missingSignerHint(tx)}</div>}
          </div>
          <div>
            <div className="text-xs font-medium">TREC Template</div>
            <TemplateSelect templates={templates} value={templateKey} onChange={setTemplateKey} testId="auto-draft-template" />
          </div>
        </div>
        {error && <div className="text-xs text-red-600 mt-3" role="alert" data-testid="auto-draft-error">{error}</div>}
        <div className="flex gap-2 mt-5 justify-end">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-lg text-slate-500 hover:bg-slate-100">Cancel</button>
          <button onClick={submit} disabled={saving} className="text-sm px-4 py-2 rounded-lg text-white disabled:opacity-60" style={{ background: '#6BA82E' }} data-testid="auto-draft-submit">{saving ? 'Drafting…' : 'Draft contract'}</button>
        </div>
      </div>
    </div>
  );
}

// The deal page's panel: every contract on this deal, plus a draft button.
export function TransactionContracts({ tx }) {
  const templates = useTemplates();
  const [rows, setRows] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [templateKey, setTemplateKey] = useState(DEFAULT_TEMPLATE);
  const [drafting, setDrafting] = useState(false);
  const id = tx.transaction_id;
  const load = () => api.get('/re/contracts', { params: { transaction_id: id } })
    .then((r) => { setRows(Array.isArray(r.data) ? r.data : []); setLoadError(''); })
    .catch((e) => { setRows([]); setLoadError(errorMessage(e)); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const makeDraft = async () => {
    if (drafting) return;
    setDrafting(true);
    try {
      await draft(id, templateKey);
      toast({ title: 'Contract drafted', description: 'Open it to check the fields and add signers.' });
      await load();
    } catch (e) {
      toast({ title: 'Could not draft the contract', description: errorMessage(e) });
    } finally { setDrafting(false); }
  };
  const preview = async (c) => {
    try { await openWithAuth(`/re/contracts/${c.contract_id}/preview.html`, { allowHtml: true }); }
    catch (e) { toast({ title: 'Could not open the preview', description: errorMessage(e) }); }
  };
  const hint = missingSignerHint(tx);

  return (
    <div data-testid="tx-contracts">
      <div className="text-[10px] uppercase tracking-widest font-bold mb-3" style={{ color: '#6BA82E' }}>Contracts</div>
      {rows === null && <div className="text-xs text-slate-400 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading…</div>}
      {loadError && <div className="text-xs text-red-600 mb-2" role="alert" data-testid="tx-contracts-error">Could not load the contracts: {loadError}</div>}
      {rows && rows.length === 0 && !loadError && <div className="text-xs text-slate-500 mb-3" data-testid="tx-contracts-empty">No contracts on this deal yet.</div>}
      <div className="space-y-2">
        {(rows || []).map((c) => (
          <div key={c.contract_id} className="flex items-center gap-3 rounded-lg border border-slate-100 p-3" data-testid={`tx-contract-${c.contract_id}`}>
            <FileSignature size={16} style={{ color: '#6BA82E' }} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate" style={{ color: '#070B5F' }}>{c.title}</div>
              <div className="text-[11px] text-slate-500">{c.signatories.length} signatories · {c.signatories.filter((s) => s.signed_at).length} signed · {c.status.replace(/_/g, ' ')}</div>
            </div>
            <button onClick={() => preview(c)} className="text-xs px-2 py-1.5 rounded border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1" data-testid={`tx-contract-preview-${c.contract_id}`}><ExternalLink size={11} /> Preview</button>
            <Link to={`/portal/real-estate?tab=contracts&contract=${c.contract_id}`} className="text-xs font-semibold px-2 py-1.5 rounded border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1" style={{ color: '#070B5F' }} data-testid={`tx-contract-open-${c.contract_id}`}><ChevronRight size={11} /> Open</Link>
          </div>
        ))}
      </div>
      <div className="mt-3 pt-3 border-t border-slate-100 flex items-end gap-2 flex-wrap">
        <div className="flex-1 min-w-[12rem]">
          <div className="text-xs font-medium">New contract from this deal</div>
          <TemplateSelect templates={templates} value={templateKey} onChange={setTemplateKey} testId="tx-contract-template" />
        </div>
        <button onClick={makeDraft} disabled={drafting} className="text-xs font-semibold px-3 py-2 rounded-lg text-white inline-flex items-center gap-1.5 disabled:opacity-60" style={{ background: '#6BA82E' }} data-testid="tx-contract-draft">
          {drafting ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Draft contract
        </button>
      </div>
      <p className="text-[11px] text-slate-500 mt-2">The draft uses the saved deal, so save your edits first. Nothing is sent to anyone.</p>
      {hint && <p className="text-[11px] text-amber-700 mt-1" data-testid="tx-contract-hint">{hint}</p>}
    </div>
  );
}
